// gauge — circular gauge with target & current values.

import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { ChartShell } from "./_chartShell";
import type { VisualProps } from "./types";
import { palette } from "../theme";
// AGENT-C: pull PBI-style defaults from the shared helper so gauges pick up
// the same Segoe UI text style + tooltip chrome as every other chart.
import { baseChartOption, isEmpty } from "./_echartsHelpers";

export function GaugeVisual({ spec, data }: VisualProps) {
  const empty = isEmpty(data) && data.value === undefined;
  const value = typeof data.value === "number" ? data.value : 0;
  const target = typeof data.target === "number" ? data.target : 100;
  const ratio = target > 0 ? Math.min(100, (value / target) * 100) : 0;

  // AGENT-C: layer the gauge-specific series on top of the shared base.
  // Gauges have no x/y axis or category legend, so the base axis / legend
  // settings are inert — but the textStyle + tooltip colors still apply.
  const base = baseChartOption();
  const option: EChartsOption = {
    ...base,
    // Gauges have no category axis — drop the axis defaults to avoid ECharts
    // rendering an empty cartesian grid behind the dial.
    xAxis: undefined,
    yAxis: undefined,
    grid: undefined,
    legend: undefined,
    series: [
      {
        type: "gauge",
        progress: { show: true, width: 12 },
        axisLine: { lineStyle: { width: 12 } },
        pointer: { show: true },
        splitLine: { distance: -18, length: 8 },
        axisTick: { show: false },
        axisLabel: { show: false },
        anchor: { show: false },
        data: [{ value: ratio }],
        detail: {
          valueAnimation: false,
          formatter: () => data.formatted ?? `${ratio.toFixed(0)}%`,
          color: palette[0],
          fontSize: 14,
        },
      },
    ],
  };

  return (
    <ChartShell title={spec.title} isEmpty={empty} status={data.status}>
      <ReactECharts
        option={option}
        notMerge
        style={{ height: "100%", width: "100%" }}
      />
    </ChartShell>
  );
}
